import { Injectable, inject } from "@angular/core";
import { HttpInterceptor, HttpHandler, HttpRequest, HTTP_INTERCEPTORS, HttpErrorResponse, HttpEvent } from "@angular/common/http";
import { Observable, throwError } from "rxjs";
import { catchError, switchMap } from "rxjs/operators";
import { AuthenticationService } from "./authentication.service";
import { AUTH_REFRESH_RETRY, SKIP_AUTH_REFRESH } from "./authentication-http-context";
import { TranslateService } from "@ngx-translate/core";

@Injectable({ providedIn: "root" })
export class HttpRequestInterceptor implements HttpInterceptor {
  private authenticationService = inject(AuthenticationService);
  private translate = inject(TranslateService);

  intercept(req: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
    const request = req.clone({
      withCredentials: true,
    });

    return next.handle(request).pipe(
      catchError((error) => {
        if (!(error instanceof HttpErrorResponse)) {
          return throwError(() => error);
        }

        const wwwAuthenticate = error.headers.get("WWW-Authenticate");
        const isRefreshCandidate = !request.context.get(SKIP_AUTH_REFRESH) && !request.context.get(AUTH_REFRESH_RETRY) && !this.isAuthenticationEndpoint(request.url) && !this.isShareTokenRequest(request) && !this.isLicenseRequest(request.url) && error.status === 401 && wwwAuthenticate != null;

        if (isRefreshCandidate) {
          if (wwwAuthenticate.includes("invalid") && !wwwAuthenticate.includes("expired")) {
            console.log(`[Interceptor] Token is invalid, clearing session...`);
            if (this.authenticationService.user) {
              this.authenticationService.clearFmLocalState(true);
            } else {
              this.authenticationService.clearToken(true);
            }
            return throwError(() => error);
          }

          return this.handle401Error(request, next, error);
        }

        if ((error.status === 400 || error.status === 401 || error.status === 409 || error.status === 500) && !request.context.get(SKIP_AUTH_REFRESH) && !request.context.get(AUTH_REFRESH_RETRY)) {
          return throwError(
            () =>
              new HttpErrorResponse({
                error: error.error,
                headers: error.headers,
                status: error.status,
                statusText: this.translate.instant("general.somethingWentWrong"),
                url: error.url ?? undefined,
              }),
          );
        }

        return throwError(() => error);
      }),
    );
  }

  private handle401Error(request: HttpRequest<any>, next: HttpHandler, originalError: HttpErrorResponse): Observable<HttpEvent<any>> {
    const isSSO = this.authenticationService.user !== null;
    const isImpersonated = this.authenticationService.getIsImpersonated();
    const replay = () => next.handle(this.replayRequest(request));

    if (isSSO == false && this.authenticationService.isOnlySsoEnvironment() && !this.authenticationService.hasFmRefreshCredentials()) {
      this.authenticationService.clearFmLocalState(true);
      return throwError(() => originalError);
    }

    if (isSSO == false && !this.authenticationService.isTokenValid()) {
      if (!this.authenticationService.hasFmRefreshCredentials()) {
        this.authenticationService.navigateToLogin(this.authenticationService.getRedirectPath());
        return throwError(() => originalError);
      }

      return this.authenticationService.refreshTokenHandler(this.authenticationService.getRedirectPath(request.url), isImpersonated).pipe(switchMap((result) => (result ? replay() : throwError(() => originalError))));
    }

    if (isSSO == true) {
      if (!isImpersonated && !this.authenticationService.isSSOTokenValid()) {
        console.log(`[Interceptor] SSO token is invalid, attempting to refresh...`);
        return this.authenticationService.refreshSSOTokenHandler(this.authenticationService.getRedirectPath(request.url)).pipe(switchMap((result) => (result ? replay() : throwError(() => originalError))));
      }

      if (isImpersonated && !this.authenticationService.isTokenValid()) {
        console.log(`[Interceptor] Token is invalid while impersonating, attempting refresh...`);
        return this.authenticationService.refreshTokenHandler(this.authenticationService.getRedirectPath(request.url), true).pipe(switchMap((result) => (result ? replay() : throwError(() => originalError))));
      }
    }

    // A concurrent request may have completed the shared refresh already. Replay with the current
    // token. The context guard prevents a second 401 from starting another refresh.
    return replay();
  }

  private replayRequest(request: HttpRequest<any>): HttpRequest<any> {
    return request.clone({
      setHeaders: {
        Authorization: this.authenticationService.headers.get("Authorization") ?? "",
      },
      context: request.context.set(AUTH_REFRESH_RETRY, true),
    });
  }

  private isAuthenticationEndpoint(url: string): boolean {
    const normalizedUrl = url.toLowerCase();
    return normalizedUrl.includes("/authentication/login") || normalizedUrl.includes("/authentication/refreshtoken");
  }

  // The licence service builds lowercase "/license/" URLs; a failed licence check must never end the session.
  private isLicenseRequest(url: string): boolean {
    return url.toLowerCase().includes("/license/");
  }

  private isShareTokenRequest(request: HttpRequest<any>): boolean {
    const shareToken = this.authenticationService.getShareToken();
    return shareToken.length > 0 && request.headers.get("Authorization") === `Bearer ${shareToken}`;
  }
}

export const httpInterceptorProviders = [{ provide: HTTP_INTERCEPTORS, useClass: HttpRequestInterceptor, multi: true }];
